// Mappers puros DTO ↔ contrato de pantalla para Políticas. Sin React ni I/O → testeables en
// aislamiento (mappers.test.ts). `toPlan` alimenta la lectura; `planToUpdateRequest` arma el body de
// PUT /credit-plans/{planId} desde el draft editado; `toPlanHistoryRow` y `toPolicyLogEntry`
// traducen un asiento de bitácora al historial del plan y al renglón del tab Auditoría.
import { CreditPlanLevelEnum } from '@fiado/type-kit/bin/loanOfferings/enums/CreditPlanLevelEnum';
import { CreditPlanStatusEnum } from '@fiado/type-kit/bin/loanOfferings/enums/CreditPlanStatusEnum';
import type {
    CreditPlanLogEntryResponse,
    PrepaymentMinimumWire,
    UpdateCreditPlanRequestWire,
} from '@/api/services/loan-offerings/contract';
import type { CreditPlanResponse } from '@/api/services/loan-offerings/loan-offerings.client';
import { formatDateMx, formatTimeMx } from '@/lib/dateHelpers';
import { INTEREST_VAT_RATE } from '@/shared/domain/quote';
import { formatMXN } from '@/shared/lib/format/money';
import type { HistoryRow } from '../components/HistoryList';
import { pesosToCents, prepaymentMinimumLabel } from '../logic';
import type { CreditPlan, PlanPrepaymentMinimum, PlanTier, PolicyLogEntry } from '../types';

/** Nivel del backend → tier de la pantalla. `ALL` (plan que aplica a todos los niveles) = empleado. */
export const LEVEL_TO_TIER: Record<CreditPlanLevelEnum, PlanTier> = {
    [CreditPlanLevelEnum.BRONZE]: 'bronce',
    [CreditPlanLevelEnum.SILVER]: 'plata',
    [CreditPlanLevelEnum.GOLD]: 'oro',
    [CreditPlanLevelEnum.ALL]: 'empleado',
};

/** El mínimo del abono del wire en unidades de la pantalla. Ausente = «Cualquier monto». */
export function toPlanPrepaymentMinimum(
    wire: PrepaymentMinimumWire | undefined,
): PlanPrepaymentMinimum {
    if (wire?.mode === 'PERCENT_OF_PERIOD_PAYMENT') {
        return { mode: wire.mode, periodPaymentPct: wire.periodPaymentPct ?? null };
    }
    if (wire?.mode === 'FIXED') {
        return {
            mode: wire.mode,
            amount: wire.amountCents === undefined ? null : wire.amountCents / 100,
        };
    }
    return { mode: 'ANY' };
}

/**
 * El mínimo de la pantalla al wire: solo el dato de su modo y el monto en cents. `null` mientras
 * falte el dato; la pantalla no deja publicar en ese estado.
 */
export function toPrepaymentMinimumWire(
    minimum: PlanPrepaymentMinimum,
): PrepaymentMinimumWire | null {
    if (minimum.mode === 'PERCENT_OF_PERIOD_PAYMENT') {
        const pct = minimum.periodPaymentPct;
        return pct === null || pct === undefined
            ? null
            : { mode: minimum.mode, periodPaymentPct: pct };
    }
    if (minimum.mode === 'FIXED') {
        const amount = minimum.amount;
        return amount === null || amount === undefined
            ? null
            : { mode: minimum.mode, amountCents: pesosToCents(amount) };
    }
    return { mode: 'ANY' };
}

/**
 * DTO del catálogo (`CreditPlanResponse`) → contrato de la pantalla (`CreditPlan` de types.ts).
 * Convierte montos (cents → pesos) y estados/niveles (enum → literal). SCI nullable → 0..100.
 * Conserva `planId` — sin él no se puede hacer PUT del plan.
 */
export function toPlan(
    dto: CreditPlanResponse & { prepaymentMinimum?: PrepaymentMinimumWire },
): CreditPlan {
    return {
        planId: dto.planId,
        tier: LEVEL_TO_TIER[dto.level],
        sciMin: dto.minSciScore ?? 0,
        sciMax: dto.maxSciScore ?? 100,
        tna: dto.tnaAnnual,
        ivaOnInterest: dto.ivaOnInterest ?? INTEREST_VAT_RATE,
        downMinPct: dto.minDownPaymentPct,
        downMaxPct: dto.maxDownPaymentPct ?? dto.minDownPaymentPct,
        maxAmount: dto.maxFinancedAmountCents / 100,
        termsWeeks: dto.termWeeksOptions,
        openingFeePct: dto.openingCommissionPct,
        active: dto.status === CreditPlanStatusEnum.ACTIVE,
        lastModified: dto.updatedAt,
        version: dto.version,
        saleTypes: dto.saleTypes,
        prepaymentMinimum: toPlanPrepaymentMinimum(dto.prepaymentMinimum),
    };
}

/**
 * El motivo tal como viaja al asiento: recortado, y AUSENTE cuando el operador no tecleó nada.
 * Mandarlo en blanco asentaría una cadena vacía, que en la columna Motivo se lee igual que un hueco.
 */
export function toAuditReason(reason: string | undefined): string | undefined {
    const trimmed = reason?.trim();
    return trimmed === undefined || trimmed === '' ? undefined : trimmed;
}

/**
 * Draft editado en la pantalla → body de PUT /credit-plans/{planId}. Inverso de `toPlan` para los
 * campos que la pantalla edita: pesos → cents (entero), fracciones 1:1.
 * `version`/`status`/`level`/`code`/`saleTypes`/`ivaOnInterest` NO se mandan: los gestiona el
 * backend (version++), son inmutables, o la pantalla no los edita. `maxDownPaymentPct` tampoco:
 * el enganche máximo quedó congelado en 95% (el backend lo fuerza igual, pero el admin ya no lo
 * toca desde aquí).
 */
export function planToUpdateRequest(
    plan: CreditPlan,
    reason?: string,
): UpdateCreditPlanRequestWire {
    const trimmed = toAuditReason(reason);
    const prepaymentMinimum = toPrepaymentMinimumWire(plan.prepaymentMinimum);
    return {
        minDownPaymentPct: plan.downMinPct,
        tnaAnnual: plan.tna,
        openingCommissionPct: plan.openingFeePct,
        maxFinancedAmountCents: Math.round(plan.maxAmount * 100),
        termWeeksOptions: plan.termsWeeks,
        minSciScore: plan.sciMin,
        maxSciScore: plan.sciMax,
        ...(prepaymentMinimum === null ? {} : { prepaymentMinimum }),
        ...(trimmed === undefined ? {} : { reason: trimmed }),
    };
}

// ---------------------------------------------------------------------------------------------
// BITÁCORA — asiento de `SharedAuditTrail` → renglón del historial del plan y del tab Auditoría.
// ---------------------------------------------------------------------------------------------

/** Unidades del valor: el asiento guarda las del plan y la pantalla lee las del operador. */
type PlanFieldFormat = 'percent' | 'cents' | 'terms' | 'plain' | 'prepaymentMinimum';

/** Los campos del plan en el lenguaje de la pantalla. Lo que no esté acá se pinta crudo. */
const PLAN_FIELD_LABEL: Record<string, string | undefined> = {
    tnaAnnual: 'Tasa anual',
    minDownPaymentPct: 'Enganche mínimo',
    maxDownPaymentPct: 'Enganche máximo',
    openingCommissionPct: 'Comisión de apertura',
    maxFinancedAmountCents: 'Monto máximo',
    termWeeksOptions: 'Plazos',
    minSciScore: 'Corte SCI mínimo',
    maxSciScore: 'Corte SCI máximo',
    prepaymentMinimum: 'Abono a capital · mínimo',
};

const PLAN_FIELD_FORMAT: Record<string, PlanFieldFormat | undefined> = {
    tnaAnnual: 'percent',
    minDownPaymentPct: 'percent',
    maxDownPaymentPct: 'percent',
    openingCommissionPct: 'percent',
    maxFinancedAmountCents: 'cents',
    termWeeksOptions: 'terms',
    minSciScore: 'plain',
    maxSciScore: 'plain',
    prepaymentMinimum: 'prepaymentMinimum',
};

/** El estado nuevo del plan dicho como acción, que es como lo lee el operador. */
const PLAN_STATUS_LABEL: Record<string, string | undefined> = {
    ACTIVE: 'Activado',
    INACTIVE: 'Inactivado',
};

/** Un valor ausente en el asiento; nunca se inventa un cero ni se deja el hueco. */
const MISSING_VALUE = '—';

/** Un asiento del mínimo del abono con un modo que la pantalla conoce. */
function isPrepaymentMinimumWire(value: unknown): value is PrepaymentMinimumWire {
    if (typeof value !== 'object' || value === null || !('mode' in value)) return false;
    return ['ANY', 'PERCENT_OF_PERIOD_PAYMENT', 'FIXED'].includes(String(value.mode));
}

/**
 * El valor del asiento en las unidades de la pantalla: fracción → porcentaje, cents → pesos,
 * plazos → lista. Un campo desconocido se pinta tal como viaja.
 */
export function formatPlanFieldValue(field: string | null, value: unknown): string {
    if (value === null || value === undefined) return MISSING_VALUE;
    const format = field === null ? undefined : PLAN_FIELD_FORMAT[field];
    if (format === 'percent' && typeof value === 'number') {
        return `${String(Number((value * 100).toFixed(2)))}%`;
    }
    if (format === 'cents' && typeof value === 'number') return formatMXN(value / 100);
    if (format === 'prepaymentMinimum' && isPrepaymentMinimumWire(value)) {
        return prepaymentMinimumLabel(toPlanPrepaymentMinimum(value));
    }
    if (format === 'terms' && Array.isArray(value)) {
        return (value as unknown[]).map((item) => String(item)).join(', ');
    }
    if (typeof value === 'string') return value;
    if (typeof value === 'number' || typeof value === 'boolean') return String(value);
    // Lo que no es escalar se pinta como viaja: interpretarlo sería adivinar.
    return JSON.stringify(value);
}

/** El campo en el idioma de la pantalla; el que no está en el diccionario va con su nombre crudo. */
export function planFieldLabel(field: string): string {
    return PLAN_FIELD_LABEL[field] ?? field;
}

/** Qué dice el renglón: el alta, la activación/inactivación, o el campo que cambió y de qué a qué. */
export function describePlanChange(entry: CreditPlanLogEntryResponse): string {
    if (entry.action === 'CREATE') return 'Plan creado';
    if (entry.action === 'STATUS_CHANGE') {
        const status = PLAN_STATUS_LABEL[String(entry.newValue)];
        if (status !== undefined) return status;
        return `Estado ${formatPlanFieldValue(null, entry.oldValue)} → ${formatPlanFieldValue(null, entry.newValue)}`;
    }
    if (entry.fieldChanged === null) return 'Plan actualizado';
    const from = formatPlanFieldValue(entry.fieldChanged, entry.oldValue);
    const to = formatPlanFieldValue(entry.fieldChanged, entry.newValue);
    return `${planFieldLabel(entry.fieldChanged)} ${from} → ${to}`;
}

/**
 * Quién tocó el plan: el nombre legible si el backend lo resolvió, y si no el `performedBy` VERBATIM
 * (hoy `system` o el `cognitoSub` crudo). No se adorna: un relleno inventa quién firmó el cambio.
 */
export function planLogAuthor(entry: CreditPlanLogEntryResponse): string {
    return entry.performedByName ?? entry.performedBy;
}

/** Asiento de bitácora → renglón de `HistoryList` (el mini-historial de UN plan). */
export function toPlanHistoryRow(entry: CreditPlanLogEntryResponse): HistoryRow {
    return {
        date: entry.occurredAt ? formatDateMx(entry.occurredAt) : MISSING_VALUE,
        kind: 'plan',
        change: describePlanChange(entry),
        reason: entry.reason ?? '',
        author: planLogAuthor(entry),
    };
}

/**
 * Asiento del feed → renglón del tab Auditoría (7 columnas). `planName` lo resuelve el backend a
 * nombre legible; cae al `planId` cuando el plan ya no está en el catálogo.
 */
export function toPolicyLogEntry(entry: CreditPlanLogEntryResponse): PolicyLogEntry {
    return {
        date: entry.occurredAt ? formatDateMx(entry.occurredAt) : MISSING_VALUE,
        time: entry.occurredAt ? formatTimeMx(entry.occurredAt) : MISSING_VALUE,
        author: planLogAuthor(entry),
        kind: 'plan',
        target: entry.planName ?? entry.planId,
        change: describePlanChange(entry),
        reason: entry.reason ?? '',
        versionFrom: entry.versionFrom,
        versionTo: entry.versionTo,
    };
}
